import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import {
  useEncryptService,
  useStorageManager,
  useTokens,
} from "../../shared/hooks";
import { toast } from "react-toastify";
import { CONSTANT } from "../../shared/constants/constant-data";
import { closeSocketConnection } from "../../components/socket/socketManager";

interface AuthContextType {
  isLoggedIn: boolean;
  login: (
    credentials: { username: string; password: string },
    newSession?: boolean
  ) => Promise<any>;
  verifyMfaLogin: (mfaToken: string, otp: string) => Promise<any>;
  resendMfaOtp: (mfaToken: string) => Promise<any>;
  logout: (broadcast?: boolean, showLogoutMsg?: boolean,timeout?:boolean) => void;
  isAuthenticated: () => boolean;
  authenticateSingleSignIn: () => Promise<any>;
  ssoLogin: (state: string, isNewSession?: boolean) => Promise<any>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

interface LoginPayload {
  username: string;
  password: string;
  ui_type: string;
  timezone: string;
  new_session: boolean;
}

const AUTH_CHANNEL_NAME = "auth_channel";

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    getToken,
    getRefreshToken,
    calculateRefreshTime,
    setAccessTokens,
    clearTokens,
  } = useTokens();
  const { doEncryptLogin, getAuthHeader } = useEncryptService();
  const { getFromLocalStorage } = useStorageManager();

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    Boolean(getFromLocalStorage("token"))
  );

  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const refreshInProgressRef = useRef<boolean>(false);
  const refreshPromiseRef = useRef<Promise<any> | null>(null);
  const authChannelRef = useRef<BroadcastChannel | null>(null);

  // Initialize the BroadcastChannel
  useEffect(() => {
    // Create the channel only once and store in ref
    authChannelRef.current = new BroadcastChannel(AUTH_CHANNEL_NAME);

    const handleBroadcast = (event: MessageEvent) => {
      if (event.data === "logout") {
        console.log("Received logout broadcast from another tab");
        // Don't broadcast again to prevent infinite loop
        performLogout(false);
      } else if (event.data === "login") {
        console.log("Received login broadcast from another tab");
        setIsLoggedIn(true);
      } else if (event.data?.type === "token" && event.data?.data) {
        console.log("Received token refresh from another tab");
        setAccessTokens(event.data.data);
        // Schedule refresh based on the new token
        scheduleTokenRefresh(event.data.data.expires_in);
      }
    };

    authChannelRef.current.addEventListener("message", handleBroadcast);

    return () => {
      authChannelRef.current?.removeEventListener("message", handleBroadcast);
      authChannelRef.current?.close();
    };
  }, []);

  // Schedule token refresh
  const scheduleTokenRefresh = (expires_in: number) => {
    const expiresIn = calculateRefreshTime(expires_in);
    const timeoutDuration = Math.max(0, expiresIn - new Date().getTime());
    if (timeoutDuration <= 0) {
      return;
    }
    cancelScheduledRefresh();

    // console.log(
    //   `Scheduling token refresh in ${Math.round(
    //     timeoutDuration / 1000 / 60
    //   )} minutes`
    // );

    refreshTimeoutRef.current = setTimeout(async () => {
      try {
        await refreshToken();
      } catch (error) {
        console.error("Scheduled token refresh failed:", error);
      }
    }, timeoutDuration);
  };

  // Cancel any scheduled refresh
  const cancelScheduledRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  // Refresh token function
  const refreshToken = async (
    refreshtoken: string | null = null
  ): Promise<any> => {
    // If refresh is already in progress, return the existing promise
    if (refreshInProgressRef.current && refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    refreshInProgressRef.current = true;

    refreshPromiseRef.current = (async () => {
      try {
        const refreshTokenValue = refreshtoken || getRefreshToken();

        if (!refreshTokenValue) {
          throw new Error("No refresh token available");
        }

        const httpOptions = getAuthHeader();
        const response = await axiosInstance.post(
          END_POINTS.REFRESH_TOKEN,
          { grant_type: "refresh_token", refresh_token: refreshTokenValue },
          httpOptions
        );

        const data = response.data.data;
        const expiresIn = calculateRefreshTime(data.expires_in);

        // Schedule next refresh
        const timeUntilRefresh = Math.max(0, expiresIn - new Date().getTime());
        if (timeUntilRefresh > 0) {
          scheduleTokenRefresh(timeUntilRefresh);
        }

        setAccessTokens(data);

        // Broadcast token refresh to other tabs
        authChannelRef.current?.postMessage({
          type: "token",
          data: data,
        });

        return data;
      } catch (error: any) {
        console.error("Token refresh failed:", error);
        // Clear auth state
        logout();

        throw new Error(
          error.response?.data?.message || "Token refresh failed: Network error"
        );
      } finally {
        refreshInProgressRef.current = false;
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  };

  const startTokenRefresh = (data: {
    access_token: string;
    expires_in: number;
  }) => {
    if (data.access_token) {
      setIsLoggedIn(true);
      // Schedule token refresh
      scheduleTokenRefresh(data.expires_in);
      // Broadcast login to other tabs
      authChannelRef.current?.postMessage("login");
      // Fetch notification counts after successful login
      window.dispatchEvent(new CustomEvent('loginSuccess'));
    }
  };

  const login = async (
    credentials: { username: string; password: string },
    newSession?: boolean
  ) => {
    try {
      const requestPayload: LoginPayload = {
        username: credentials.username.toLowerCase(),
        password: doEncryptLogin(credentials.password),
        ui_type: "spark",
        timezone: "UTC",
        new_session: newSession !== undefined ? newSession : true,
      };

      const httpOptions = getAuthHeader();
      const response = await axiosInstance.post(
        END_POINTS.LOGIN,
        requestPayload,
        httpOptions
      );

      const data = response.data.data;

      // Email MFA: tokens are not issued until OTP verification succeeds.
      if (data?.mfa_required) {
        return response.data;
      }

      startTokenRefresh(data);

      return response.data;
    } catch (error: any) {
      // Keep the response so the login page can read the change-password link out of a 403.
      throw Object.assign(new Error(error.response?.data?.message || "Login failed"), {
        response: error.response,
      });
    }
  };

  const verifyMfaLogin = async (mfaToken: string, otp: string) => {
    try {
      const httpOptions = getAuthHeader();
      const response = await axiosInstance.post(
        END_POINTS.MFA,
        { action: "verify", mfa_token: mfaToken, otp },
        httpOptions
      );
      const data = response.data.data;
      if (!data?.mfa_required && data?.access_token) {
        startTokenRefresh(data);
      }
      return response.data;
    } catch (error: any) {
      throw Object.assign(
        new Error(error.response?.data?.message || "Verification failed"),
        { response: error.response }
      );
    }
  };

  const resendMfaOtp = async (mfaToken: string) => {
    try {
      const httpOptions = getAuthHeader();
      const response = await axiosInstance.post(
        END_POINTS.MFA,
        { action: "resend", mfa_token: mfaToken },
        httpOptions
      );
      return response.data;
    } catch (error: any) {
      throw Object.assign(
        new Error(error.response?.data?.message || "Unable to resend code"),
        { response: error.response }
      );
    }
  };

  const ssoLogin = async (state: string, isNewSession: boolean = false) => {
    const req: any = {
      state: state,
    };
    if (isNewSession) {
      req.new_session = isNewSession;
    }
    const token = localStorage.getItem("token");
    if(token){
      req.access_token= token.replace(/"/g, "");
      //const token = localStorage.getItem("token");
    }
    const httpOptions = getAuthHeader();
    const res = await axiosInstance.post(
      END_POINTS.SSO_LOGIN,
      req,
      httpOptions
    );

    const data = res.data.data;
    startTokenRefresh(data);
    return res.data;
  };

  const authenticateSingleSignIn = async () => {
    const payload = {
      sso_code: "microsoft_sso",
    };

    try {
      const response = await axiosInstance.post(
        END_POINTS.AUTHENTICATE_SINGLE_SIGNIN,
        payload
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "SSO authentication failed"
      );
    }
  };

  // Separate the logout logic from the broadcast mechanism
  const performLogout = useCallback(
    (broadcast: boolean = true,timeout:boolean=false) => {
      cancelScheduledRefresh();
      if(timeout){
        clearTokens(true);
      } else {
      clearTokens();
      }
      setIsLoggedIn(false);
      closeSocketConnection();
      if (broadcast) {
        authChannelRef.current?.postMessage("logout");
      }
    },
    [cancelScheduledRefresh, clearTokens]
  );

  // const logout = useCallback(
  //   (broadcast: boolean = true, showLogoutMsg: boolean = false,timeout:boolean=false) => {
  //     const token = getToken();
  //     if (!token) {
  //       performLogout(broadcast,timeout);
  //       return;
  //     }

  //     const httpOptions = getAuthHeader();

  //     axiosInstance
  //       .get(`${END_POINTS.LOGOUT}?token=${token}`, httpOptions)
  //       .then(() => {
  //         performLogout(broadcast,timeout);
  //          window.location.href = window.location.href; // Hard refresh
  //       })
  //       .catch((error) => {
  //         console.error("Logout API call failed:", error);
  //         performLogout(broadcast,timeout);
  //          window.location.href = window.location.href; // Hard refresh
  //         if (showLogoutMsg) {
  //           toast.error(CONSTANT.LOGOUT_DEVICE);
  //         }
  //       });
  //   },
  //   [getToken, getAuthHeader, performLogout]
  // );

  const logout = useCallback(
  (broadcast: boolean = true, showLogoutMsg: boolean = false, timeout: boolean = false) => {
    const token = getToken();
    if (!token) {
      performLogout(broadcast, timeout);
      return;
    }

    const httpOptions = getAuthHeader();

    axiosInstance
      .get(`${END_POINTS.LOGOUT}?token=${token}`, httpOptions)
      .then((response) => {
        if (response.status === 200) {
          performLogout(broadcast, timeout);
          window.location.reload(); // ✅ Refresh only on 200
        }
      })
      .catch((error) => {
        console.error("Logout API call failed:", error);
        performLogout(broadcast, timeout);
        if (showLogoutMsg) {
          toast.error(CONSTANT.LOGOUT_DEVICE);
        }
      });
  },
  [getToken, getAuthHeader, performLogout]
);


  const isAuthenticated = useCallback(() => {
    return Boolean(getToken());
  }, [getToken]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        login,
        verifyMfaLogin,
        resendMfaOtp,
        logout,
        isAuthenticated,
        authenticateSingleSignIn,
        ssoLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
